import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AncDateRangePreset, AncDateRangeValue } from '../anc-filter.models';
import { DEFAULT_PRESETS } from './date-presets';
import {
    CalendarDay,
    addMonths,
    buildMonthGrid,
    fromIso,
    isInRange,
    toIso
} from './calendar';
import { AncButtonDirective } from '../../anc-button/anc-button.directive';

/** Which picker surface is showing. Clicking the header steps days → months → years. */
type AncCalView = 'days' | 'months' | 'years';

@Component({
    selector: 'app-anc-date-range',
    imports: [CommonModule, TranslateModule, AncButtonDirective],
    templateUrl: './anc-date-range.component.html',
    styleUrls: ['./anc-date-range.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncDateRangeComponent implements OnChanges {
    @Input() value: AncDateRangeValue | null = null;
    /** 'range' = start+end (default); 'single' = one date. */
    @Input() mode: 'range' | 'single' = 'range';
    @Input() presets: AncDateRangePreset[] = DEFAULT_PRESETS;
    @Input() minDate?: string;
    @Input() maxDate?: string;

    @Output() valueChange = new EventEmitter<AncDateRangeValue | null>();

    draftStart: string | null = null;
    draftEnd: string | null = null;
    hovered: string | null = null;
    activePreset?: string;
    monthAnchor: string = toIso(new Date());
    view: AncCalView = 'days';

    readonly weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    readonly months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ];

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value']) this.seedFromValue();
    }

    get showPresets(): boolean {
        return this.mode === 'range' && this.presets?.length > 0;
    }

    // ── Days view ────────────────────────────────────────────────────────────
    get grid(): CalendarDay[] {
        return buildMonthGrid(this.monthAnchor);
    }

    get monthLabel(): string {
        const d = fromIso(this.monthAnchor);
        return `${this.months[d.getMonth()]} ${d.getFullYear()}`;
    }

    get anchorYear(): number {
        return fromIso(this.monthAnchor).getFullYear();
    }

    /** 12-year window (3×4) for the year picker, anchored to a decade-ish band. */
    get yearGrid(): number[] {
        const start = this.anchorYear - 6;
        return Array.from({ length: 12 }, (_, i) => start + i);
    }

    prevMonth(): void {
        this.monthAnchor = addMonths(this.monthAnchor, -1);
    }
    nextMonth(): void {
        this.monthAnchor = addMonths(this.monthAnchor, 1);
    }

    /** Header arrows are view-aware: page months / years when in those views. */
    prev(): void {
        if (this.view === 'days') this.prevMonth();
        else if (this.view === 'months') this.shiftYear(-1);
        else this.shiftYear(-12);
    }
    next(): void {
        if (this.view === 'days') this.nextMonth();
        else if (this.view === 'months') this.shiftYear(1);
        else this.shiftYear(12);
    }

    private shiftYear(delta: number): void {
        const d = fromIso(this.monthAnchor);
        this.monthAnchor = toIso(
            new Date(d.getFullYear() + delta, d.getMonth(), 1)
        );
    }

    /** Header label click: days → months → years. */
    stepView(): void {
        this.view = this.view === 'days' ? 'months' : 'years';
    }

    isCurrentMonth(i: number): boolean {
        return i === fromIso(this.monthAnchor).getMonth();
    }

    selectMonth(i: number): void {
        const d = fromIso(this.monthAnchor);
        this.monthAnchor = toIso(new Date(d.getFullYear(), i, 1));
        this.view = 'days';
    }

    selectYear(y: number): void {
        const d = fromIso(this.monthAnchor);
        this.monthAnchor = toIso(new Date(y, d.getMonth(), 1));
        this.view = 'months';
    }

    isDisabled(iso: string): boolean {
        if (this.minDate && iso < this.minDate) return true;
        if (this.maxDate && iso > this.maxDate) return true;
        return false;
    }

    isStart(iso: string): boolean {
        return iso === this.draftStart;
    }
    isEnd(iso: string): boolean {
        if (this.mode === 'single') return false;
        return iso === (this.draftEnd ?? this.previewEnd);
    }
    inRange(iso: string): boolean {
        if (this.mode === 'single') return false;
        return isInRange(
            iso,
            this.draftStart,
            this.draftEnd ?? this.previewEnd
        );
    }

    /** Tentative end while picking the second date (hover preview). */
    private get previewEnd(): string | null {
        if (
            this.draftStart &&
            !this.draftEnd &&
            this.hovered &&
            this.hovered >= this.draftStart
        ) {
            return this.hovered;
        }
        return null;
    }

    onHover(iso: string): void {
        if (this.mode === 'single' || !this.draftStart || this.draftEnd) return;
        this.hovered = iso;
        this.cdr.markForCheck();
    }

    selectDay(iso: string): void {
        if (this.isDisabled(iso)) return;
        this.activePreset = undefined;

        if (this.mode === 'single') {
            this.draftStart = iso;
            this.draftEnd = iso;
            return;
        }

        // Range: first click (or restart after a complete range) sets the start.
        if (!this.draftStart || this.draftEnd) {
            this.draftStart = iso;
            this.draftEnd = null;
            this.hovered = null;
        } else if (iso < this.draftStart) {
            // Clicked before the start → treat as the new start.
            this.draftStart = iso;
        } else {
            this.draftEnd = iso;
        }
    }

    applyPreset(p: AncDateRangePreset): void {
        const r = p.range();
        this.draftStart = r.start;
        this.draftEnd = r.end;
        this.activePreset = p.id;
        this.monthAnchor = r.start;
        this.view = 'days';
    }

    get canApply(): boolean {
        return !!this.draftStart && (this.mode === 'single' || !!this.draftEnd);
    }

    apply(): void {
        if (!this.canApply) return;
        const end = this.mode === 'single' ? this.draftStart! : this.draftEnd!;
        this.valueChange.emit({
            start: this.draftStart!,
            end,
            preset: this.activePreset
        });
    }

    clear(): void {
        this.draftStart = null;
        this.draftEnd = null;
        this.hovered = null;
        this.activePreset = undefined;
        this.valueChange.emit(null);
    }

    private seedFromValue(): void {
        this.draftStart = this.value?.start ?? null;
        this.draftEnd = this.value?.end ?? null;
        this.hovered = null;
        this.activePreset = this.value?.preset;
        this.monthAnchor = this.value?.start ?? toIso(new Date());
        this.view = 'days';
    }
}
